/**
 * useBlindMode — Sprint 2 / TASK 4-2
 *
 * Single source of truth for the user's blind-mode preference:
 *   • Reads the current value from `user_profiles.blind_mode` on mount.
 *   • Exposes `toggle()` which writes back to the DB (optimistic update).
 *   • Broadcasts the new state via a `CustomEvent` so other tabs / components
 *     (e.g. the memoised JobCard list) can react without prop-drilling.
 *
 * Hard rules:
 *   • Blind mode is persisted in DB, NOT just localStorage.
 *   • `toggle()` is safe to call before the initial load completes — it
 *     queues the DB write, which completes with the correct final value.
 */

import { useCallback, useEffect, useState } from 'react';

import { supabase } from '../../../lib/supabase';

/** Window-level event so all consumers stay in sync within the same tab. */
const BLIND_MODE_EVENT = 'ggj:blind-mode-change';

export function dispatchBlindModeChange(enabled: boolean): void {
  window.dispatchEvent(
    new CustomEvent<{ enabled: boolean }>(BLIND_MODE_EVENT, { detail: { enabled } }),
  );
}

export interface UseBlindModeReturn {
  /** Current blind-mode state. `null` while the initial DB load is pending. */
  blindMode: boolean | null;
  /** Optimistically toggles blind mode and persists to `user_profiles`. */
  toggle: () => void;
  /** True during the initial DB fetch. */
  loading: boolean;
}

export function useBlindMode(): UseBlindModeReturn {
  const [blindMode, setBlindMode] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(true);

  // ── Initial load from DB ─────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (cancelled) return;
      if (!session?.user?.id) {
        setBlindMode(false);
        setLoading(false);
        return;
      }
      const { data, error } = await supabase
        .from('user_profiles')
        .select('blind_mode')
        .eq('id', session.user.id)
        .maybeSingle<{ blind_mode: boolean | null }>();

      if (cancelled) return;
      if (error) {
        console.warn('[useBlindMode] fetch error:', error.message);
        setBlindMode(false);
      } else {
        setBlindMode(data?.blind_mode ?? false);
      }
      setLoading(false);
    };

    void run();
    return () => { cancelled = true; };
  }, []);

  // ── Listen for cross-component updates in the same tab ───────────────────
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<{ enabled: boolean }>).detail;
      setBlindMode(detail.enabled);
    };
    window.addEventListener(BLIND_MODE_EVENT, handler);
    return () => window.removeEventListener(BLIND_MODE_EVENT, handler);
  }, []);

  // ── Toggle ───────────────────────────────────────────────────────────────
  const toggle = useCallback(() => {
    setBlindMode((prev) => {
      const next = !(prev ?? false);

      // Persist best-effort (non-blocking). Use upsert so it works even if
      // the user has no user_profiles row yet (e.g. onboarding not complete).
      void (async () => {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.user?.id) return;
        const { error } = await supabase
          .from('user_profiles')
          .upsert({ id: session.user.id, blind_mode: next }, { onConflict: 'id' });
        if (error) {
          console.warn('[useBlindMode] persist error:', error.message);
        }
      })();

      // Broadcast so other hooks/components sync without re-fetching.
      dispatchBlindModeChange(next);
      return next;
    });
  }, []);

  return { blindMode, loading, toggle };
}

// ─────────────────────────────────────────────────────────────────────────────
// Lightweight subscriber — for components that only need to read the current
// value without owning the DB write logic.
// ─────────────────────────────────────────────────────────────────────────────
export function useBlindModeValue(): boolean {
  const [enabled, setEnabled] = useState<boolean>(false);

  useEffect(() => {
    let cancelled = false;

    // One-shot load.
    const load = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (cancelled || !session?.user?.id) return;
      const { data } = await supabase
        .from('user_profiles')
        .select('blind_mode')
        .eq('id', session.user.id)
        .maybeSingle<{ blind_mode: boolean | null }>();
      if (!cancelled) setEnabled(data?.blind_mode ?? false);
    };
    void load();

    // Keep in sync with toggle() calls elsewhere in the same tab.
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<{ enabled: boolean }>).detail;
      if (!cancelled) setEnabled(detail.enabled);
    };
    window.addEventListener(BLIND_MODE_EVENT, handler);
    return () => {
      cancelled = true;
      window.removeEventListener(BLIND_MODE_EVENT, handler);
    };
  }, []);

  return enabled;
}
